import { CommandGroup, CommandItem } from '@/components/ui/command';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import {
  useAssignTaskMutation,
  useAssignTaskMutationV2,
} from '@/hooks/use-assign-task';
import {
  useCreateTaskMutation,
  useCreateTaskTemplateMutation,
} from '@/hooks/use-create-task';
import useTasks from '@/hooks/use-tasks';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import { Deal } from '@/models/registry';
import { TaskableType } from '@/models/types.ts';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark, Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../../ui/button';
import { ComboboxPopover } from '../../ui/combobox/combobox-popover';
import useTasksStats from './hooks/use-tasks-stats';
import TaskDialog from './task-dialog';
import TasksFraction from './tasks-fraction';
import TasksProgressRing from './tasks-progress-ring';

const DealTaskSelector = observer(
  ({
    deal,
    withHokeyListener = false,
  }: {
    deal: Deal;
    withHokeyListener?: boolean;
  }) => {
    const assignedTasks = deal.assigned_tasks || [];
    const { tasks } = useTasks();
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [comboBoxPopoverIsOpen, setComboBoxPopoverIsOpen] = useState(false);

    const taskOptions = useMemo(() => {
      const options = [];
      for (const task of tasks) {
        options.push({
          value: task.id,
          id: task.id,
          label: task.title,
          description: task.description,
          icon: <Landmark className="h-3.5 w-3.5" />,
        });
      }
      return options;
    }, [tasks]);

    const queryClient = useQueryClient();

    const [titleValue, setTitleValue] = useState('');
    const [descriptionValue, setDescriptionValue] = useState('');

    const createTaskMutation = useCreateTaskMutation();
    const assignTaskMutation = useAssignTaskMutation();
    const createTaskTemplateMutation = useCreateTaskTemplateMutation();
    const assignTaskMutationV2 = useAssignTaskMutationV2();

    const handleCreateTaskSuccess = () => {
      setDialogIsOpen(false);
      setTitleValue('');
      setDescriptionValue('');
    };

    const handleAssignTask = (value: string) => {
      const innerTask = tasks.find((task) => task.id === value);
      if (!innerTask) {
        return;
      } else {
        assignTaskMutation.mutate({
          task: {
            id: uuidv4(),
            task_id: innerTask.id,
            deal_id: deal.id,
            title: innerTask.title,
            description: innerTask.description,
            status: 'incomplete',
          },
          dealId: deal.id,
        });
        assignTaskMutationV2.mutate({
          task: {
            id: uuidv4(),
            task_template_id: innerTask.id,
            taskable_type: TaskableType.Deal,
            taskable_id: deal.id,
            title: innerTask.title,
            description: innerTask.description,
            status: 'incomplete',
          },
          taskableId: deal.id,
          taskableType: TaskableType.Deal,
        });
      }
    };

    const { hasAssignedTasks } = useTasksStats(assignedTasks);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const onHotkeyPressed = useCallback(() => {
      buttonRef.current?.click();
    }, []);

    return (
      <div
        className={cn('flex flex-row gap-2 w-full px-2', {
          'justify-between': hasAssignedTasks,
        })}
      >
        {hasAssignedTasks && (
          <div className="flex flex-row gap-2 items-center">
            <div className="text-xs  text-gray-500 text-nowrap">Tasks</div>
            <TasksFraction assignedTasks={assignedTasks} />
            <TasksProgressRing assignedTasks={assignedTasks} />
          </div>
        )}
        <div>
          <ComboboxPopover
            open={comboBoxPopoverIsOpen}
            onOpenChange={setComboBoxPopoverIsOpen}
            endAdornment={
              <KeyboardKeyIcon>{HOTKEYS.DEAL_ADD_TASK}</KeyboardKeyIcon>
            }
            placeholder="Set task..."
            onChange={handleAssignTask}
            emptyMessage="No task found"
            options={taskOptions}
            footerOptions={
              <CommandGroup>
                <p className="p-2 rounded text-xs  text-gray-600">New task</p>
                <CommandItem
                  onSelect={() => {
                    setComboBoxPopoverIsOpen(false);
                    setDialogIsOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <p className="rounded text-xs  text-gray-600">New task</p>
                </CommandItem>
              </CommandGroup>
            }
            side={hasAssignedTasks ? 'left' : 'right'}
            align="start"
          >
            <div>
              <Button
                variant="ghost"
                className="px-0 py-1 h-fit"
                ref={buttonRef}
              >
                {!hasAssignedTasks ? (
                  <div className="flex flex-row gap-1 items-center">
                    <Plus
                      className={cn(
                        'h-3.5 w-3.5 cursor-pointer text-gray-500',
                        {},
                      )}
                    />
                    <div className="text-xs  text-gray-500 text-nowrap">
                      Add task
                    </div>
                  </div>
                ) : (
                  <Plus
                    className={cn(
                      'h-3.5 w-3.5 cursor-pointer text-gray-500',
                      {},
                    )}
                  />
                )}
              </Button>

              {withHokeyListener && (
                <HotKeyListener
                  hotkeys={HOTKEYS.DEAL_ADD_TASK}
                  onHotkeyPressed={onHotkeyPressed}
                />
              )}
            </div>
          </ComboboxPopover>
          <TaskDialog
            titleValue={titleValue}
            setTitleValue={setTitleValue}
            descriptionValue={descriptionValue}
            setDescriptionValue={setDescriptionValue}
            onClickCta={({ titleValue, descriptionValue }) => {
              createTaskMutation.mutate(
                {
                  task: {
                    id: uuidv4(),
                    title: titleValue,
                    description: descriptionValue,
                  },
                  dealId: deal.id,
                },
                {
                  onSuccess: handleCreateTaskSuccess,
                  onSettled: () => {
                    queryClient.invalidateQueries({ queryKey: ['tasks'] });
                  },
                },
              );
              createTaskTemplateMutation.mutate({
                taskTemplate: {
                  id: uuidv4(),
                  title: titleValue,
                  description: descriptionValue,
                  taskable_type: TaskableType.Deal,
                },
                taskableId: deal.id,
                taskableType: TaskableType.Deal,
              });
            }}
            ctaText={'Create Task'}
            onOpen={() => setDialogIsOpen(true)}
            onClose={() => setDialogIsOpen(false)}
            dialogIsOpen={dialogIsOpen}
          ></TaskDialog>
        </div>
      </div>
    );
  },
);

export default DealTaskSelector;
